/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import * as React from "react";
import styled from "styled-components";

import Checkbox from "../../../reusable/checkbox";
import TextInputField from "../../../reusable/textInputField";
import { DenominationsPropTypes } from "../lib/types";
import {
  StyledButton,
  StyledDeleteButton,
  StyledDeleteImage,
  StyledHelperText,
  StyledLabel,
  StyledRequired,
} from "../lib/styles";
import { denominationStatesDisplay } from "../lib/constants";
import { getInactiveReasonsDisplay } from "../lib/helpers";
import { redesignTheme, theme } from "../../../../components/theme/theme";

const trash = require("../../../svg/trash.svg");

const StyledDelete = styled(StyledDeleteButton)`
  right: 0;
  top: 0;
`;

const StyledTable = styled.table`
  table-layout: auto;
  width: 100%;
  margin-bottom: 14px;
`;

const StyledTableHeader = styled.th`
  font-family: ${theme.fontFamily};
  font-weight: ${theme.fontWeight.bold};
  color: ${theme.color.gray800};
  font-size: ${theme.fontSize.s};
  padding-bottom: 14px;
  padding-right: 14px;
  white-space: nowrap;
`;

const StyledHeaderLast = styled(StyledTableHeader)`
  padding-right: 0px;
`;

const StyledTableData = styled.td`
  padding-bottom: 14px;
  padding-right: 14px;
`;

const StyledTableDataLast = styled.td`
  padding-bottom: 14px;
`;

const Denominations = ({
  currencySymbol,
  denominations,
  disabled,
  handleFieldChange,
  isEditing,
}: DenominationsPropTypes): React.ReactElement => {
  return (
    <>
      <StyledLabel>
        Denominations<StyledRequired>*</StyledRequired>
      </StyledLabel>

      <StyledHelperText>
        External IDs are optional. They are the UTID for Tango and product ID
        for Tremendous. External IDs may be the same across all denominations or
        unique for each denomination. Enter a unique ID for each amount if Tango
        catalog items are &quot;Fixed&quot; value type. Enter the same ID for
        each amount if Tango catalog items are &quot;Variable&quot; value type.
      </StyledHelperText>

      <StyledTable>
        <thead>
          <tr>
            <StyledTableHeader sr-only="Currency Symbol" />
            <StyledTableHeader>Amount</StyledTableHeader>
            <StyledTableHeader>External ID</StyledTableHeader>
            {isEditing && <StyledTableHeader>State</StyledTableHeader>}
            {isEditing && <StyledHeaderLast>Inactive Reasons</StyledHeaderLast>}
            {!isEditing && <StyledHeaderLast sr-only="Delete" />}
          </tr>
        </thead>
        <tbody>
          {denominations.map((denomination, index) => {
            const newDenom = !denomination.state;
            return (
              <tr key={`denomination-${index}`}>
                <StyledTableData>{currencySymbol}</StyledTableData>
                <StyledTableData>
                  <TextInputField
                    disabled={
                      disabled ||
                      denomination.state === "archived" ||
                      (isEditing && !newDenom)
                    }
                    handleChange={(event): void => {
                      const updatedDenominations = [...denominations];
                      updatedDenominations[
                        index
                      ].amount = event.target.value.replace(/[^0-9.]/g, "");
                      handleFieldChange({
                        denominations: updatedDenominations,
                      });
                    }}
                    id={`denomination-amount-${index}`}
                    isRequired
                    placeholderText="10"
                    value={denomination.amount}
                  />
                </StyledTableData>
                <StyledTableData>
                  <TextInputField
                    disabled={disabled || denomination.state === "archived"}
                    handleChange={(event): void => {
                      const updatedDenominations = [...denominations];
                      updatedDenominations[index].externalIdentifier =
                        event.target.value;
                      handleFieldChange({
                        denominations: updatedDenominations,
                      });
                    }}
                    id={`denomination-externalid-${index}`}
                    value={denomination.externalIdentifier}
                  />
                </StyledTableData>
                {isEditing && (
                  <>
                    <StyledTableData
                      data-testid={`denomination-state-${index}`}
                      style={{
                        color:
                          denomination.state === "archived"
                            ? redesignTheme.color.gray500
                            : redesignTheme.color.gray900,
                      }}
                    >
                      {denominationStatesDisplay[denomination.state] ||
                        "Unknown"}
                    </StyledTableData>
                    <StyledTableDataLast
                      data-testid={`denomination-inactive-reasons-${index}`}
                      style={{
                        color:
                          denomination.state === "archived"
                            ? redesignTheme.color.gray500
                            : redesignTheme.color.gray900,
                      }}
                    >
                      {getInactiveReasonsDisplay(denomination.inactiveReasons)}
                    </StyledTableDataLast>
                  </>
                )}
                {denomination.state !== "archived" && (
                  <StyledTableDataLast>
                    <StyledDelete
                      disabled={disabled}
                      onClick={(): void => {
                        const updatedDenominations = [...denominations];
                        updatedDenominations.splice(index, 1);

                        handleFieldChange({
                          denominations: updatedDenominations,
                        });
                      }}
                      size="small"
                      testId={`remove-denomination-${index}`}
                      variant="secondary"
                    >
                      <StyledDeleteImage src={trash} alt="trash icon" />
                    </StyledDelete>
                  </StyledTableDataLast>
                )}
              </tr>
            );
          })}
        </tbody>
      </StyledTable>

      <StyledButton
        disabled={disabled}
        onClick={(): void => {
          const updatedDenominations = [
            ...denominations,
            {
              amount: "",
              externalIdentifier: "",
            },
          ];
          handleFieldChange({ denominations: updatedDenominations });
        }}
        size="small"
        testId="add-denomination-button"
        variant="primary"
      >
        Add Denomination
      </StyledButton>
    </>
  );
};

Denominations.defaultProps = {
  currencySymbol: "$",
};

export default Denominations;
